'use strict';

/**
 * Home -- the cross-tenant "Your plan sheets" screen (WI #339). It is the default landing after
 * sign-in and the canonical place to reach every plan sheet you belong to, no matter which one the
 * session is currently in. It renders tenant-less: its only data source is GET /1.0/Account/Tenants
 * (which reads across the whole account), never the IDCustomer-scoped entity providers, so it is safe
 * to show while "between" tenants and is registered as a tenant-safe area root.
 *
 * Three view modes off one segmented control: Compact list (default), Expanded list (adds the plan
 * sheet's description + persona line), and Cards (full-width, each fronted by the plan sheet's cover
 * blueprint -- the navy title block, mirrored from BlueprintRender, or a pick-a-blueprint placeholder
 * when none is set). Two sections: Pinned on top, then All by recency. A search box filters by name,
 * code, and description.
 *
 * The shell (header + section containers) is a static template; the rows/cards are painted
 * imperatively into #psh-pinned / #psh-all by _paint(). Repainting only those containers -- not the
 * whole view -- keeps the search box focused and its caret in place as you type, and lets a pin toggle
 * re-sort in place without a server round-trip losing your scroll.
 */

const libPictView = require('pict-view');
const libFrameworkClient = require('../providers/FrameworkClient.js');
const libPlansheetSort = require('../PlansheetSort.js');

function _escapeHTML(pValue)
{
	return String(pValue == null ? '' : pValue)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// The one-letter mark for a plan sheet with no cover (the little navy tile in list rows): the first
// two alphanumerics of its name, upper-cased.
function _mark(pName)
{
	return String(pName || '?').replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase() || '?';
}

// The glyph letter for a blueprint pin row, from the artifact's ItemType (Vision -> V, Module -> M,
// Feature -> F, ...). Falls back to the first letter of the type.
function _typeGlyph(pType)
{
	let tmpType = String(pType || '');
	return (tmpType.charAt(0) || '*').toUpperCase();
}

class PictViewHome extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		// View state, kept on the instance so a repaint (mode / search / pin) never refetches.
		this._tenants = [];
		this._mode = 'compact';   // 'compact' | 'expanded' | 'cards'
		this._query = '';
		this._loaded = false;
	}

	// Load the account's plan sheets (cross-tenant), then render the page.
	show()
	{
		return this._loadTenants().then(() => this.render()).catch(() => this.render());
	}

	_loadTenants()
	{
		return libFrameworkClient.getJSON(this.pict, '/1.0/Account/Tenants').then((pBody) =>
		{
			this._tenants = (pBody && Array.isArray(pBody.Tenants)) ? this._sortTenants(pBody.Tenants) : [];
			this._loaded = true;
		}).catch(() =>
		{
			this._tenants = [];
			this._loaded = true;
		});
	}

	// The same pinned-first-then-recency order the server returns (shared PlansheetSort), applied here
	// too so a local pin toggle re-sorts in place without a refetch. Mutates in place and returns it.
	_sortTenants(pList)
	{
		return libPlansheetSort.sortPlansheets(pList);
	}

	// A compact "how long ago" label (now, 5m, 3h, 2d, 4mo, 1y); '' when never visited.
	_ago(pValue)
	{
		if (!pValue) { return ''; }
		let tmpMs = Date.parse(String(pValue).replace(' ', 'T'));
		if (isNaN(tmpMs)) { return ''; }
		let tmpSec = Math.max(0, Math.floor((Date.now() - tmpMs) / 1000));
		if (tmpSec < 60) { return 'now'; }
		let tmpMin = Math.floor(tmpSec / 60);
		if (tmpMin < 60) { return tmpMin + 'm'; }
		let tmpHr = Math.floor(tmpMin / 60);
		if (tmpHr < 24) { return tmpHr + 'h'; }
		let tmpDay = Math.floor(tmpHr / 24);
		if (tmpDay < 30) { return tmpDay + 'd'; }
		let tmpMon = Math.floor(tmpDay / 30);
		if (tmpMon < 12) { return tmpMon + 'mo'; }
		return Math.floor(tmpMon / 12) + 'y';
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		this._syncSegButtons();
		this._paint();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	// --- interaction ---------------------------------------------------------

	setMode(pMode)
	{
		let tmpMode = (pMode === 'expanded' || pMode === 'cards') ? pMode : 'compact';
		if (tmpMode === this._mode) { return; }
		this._mode = tmpMode;
		this._syncSegButtons();
		this._paint();
	}

	filter(pValue)
	{
		this._query = String(pValue == null ? '' : pValue);
		this._paint();
	}

	// Enter a plan sheet: the current one just goes to its board; any other switches the session into
	// it and reloads the app under that tenant (reduced to a tenant-safe route first; see WI #328).
	openSheet(pIDCustomer)
	{
		let tmpID = parseInt(pIDCustomer, 10) || 0;
		let tmpTenant = this._tenants.find((pT) => pT.IDCustomer === tmpID);
		if (tmpTenant && tmpTenant.IsCurrent) { this._navigate('/board'); return; }
		this.switchTenant(tmpID);
	}

	switchTenant(pIDCustomer)
	{
		let tmpID = parseInt(pIDCustomer, 10) || 0;
		if (!tmpID) { return; }
		libFrameworkClient.postJSON(this.pict, '/1.0/Session/Context/' + tmpID, {}).then((pBody) =>
		{
			if (pBody && pBody.Switched) { this._reloadForTenantChange(); return; }
			this._toast((pBody && pBody.Error) || 'Could not open that plan sheet.');
		}).catch(() => this._toast('Could not open that plan sheet.'));
	}

	// Pin or unpin, then re-sort + repaint in place. Optimistic would risk drift on a failed write, so
	// we flip local state only once the server confirms.
	togglePin(pIDCustomer)
	{
		let tmpID = parseInt(pIDCustomer, 10) || 0;
		let tmpTenant = this._tenants.find((pT) => pT.IDCustomer === tmpID);
		if (!tmpTenant) { return; }
		let tmpNext = !tmpTenant.Pinned;
		libFrameworkClient.postJSON(this.pict, '/1.0/Account/Tenants/' + tmpID + '/Pin', { Pinned: tmpNext }).then((pBody) =>
		{
			if (pBody && pBody.Success)
			{
				tmpTenant.Pinned = !!pBody.Pinned;
				this._sortTenants(this._tenants);
				this._paint();
				return;
			}
			this._toast((pBody && pBody.Error) || 'Could not update the pin.');
		}).catch(() => this._toast('Could not update the pin.'));
	}

	_navigate(pRoute)
	{
		let tmpApp = this.pict && this.pict.PictApplication;
		if (tmpApp && typeof tmpApp._navigate === 'function') { tmpApp._navigate(pRoute); return; }
		if (typeof window !== 'undefined' && window.location) { window.location.hash = '#' + String(pRoute || '/').replace(/^#/, ''); }
	}

	_reloadForTenantChange()
	{
		let tmpApp = this.pict && this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.reloadForActivePlansheetChange === 'function') { tmpApp.reloadForActivePlansheetChange(); return; }
		if (typeof window !== 'undefined' && window.location) { window.location.reload(); }
	}

	_toast(pMessage)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast(pMessage, { type: 'error' }); }
		else if (typeof window !== 'undefined') { window.alert(pMessage); }
	}

	// --- painting ------------------------------------------------------------

	// Reflect the active mode on the segmented control and on the two section containers (the density
	// class the list CSS keys off; the cards class swaps the layout).
	_syncSegButtons()
	{
		if (typeof document === 'undefined') { return; }
		let tmpRoot = document.getElementById('Plansheet-Home-Root');
		if (!tmpRoot) { return; }
		let tmpButtons = tmpRoot.querySelectorAll('.psh-seg button');
		for (let i = 0; i < tmpButtons.length; i++)
		{
			let tmpOn = (tmpButtons[i].getAttribute('data-mode') === this._mode);
			tmpButtons[i].classList.toggle('on', tmpOn);
		}
	}

	_matches(pTenant)
	{
		let tmpQuery = this._query.trim().toLowerCase();
		if (!tmpQuery) { return true; }
		let tmpHay = (String(pTenant.CustomerName || '') + ' ' + String(pTenant.Code || '') + ' ' + String(pTenant.Description || '')).toLowerCase();
		return tmpHay.indexOf(tmpQuery) >= 0;
	}

	_paint()
	{
		if (typeof document === 'undefined') { return; }
		let tmpAll = this._tenants.filter((pT) => this._matches(pT));
		let tmpPinned = tmpAll.filter((pT) => pT.Pinned);
		let tmpModeClass = (this._mode === 'cards') ? 'psh-cards' : ('psh-list psh-' + this._mode);
		let tmpCards = (this._mode === 'cards');

		let fFill = (pID, pList, pEmptyText) =>
		{
			let tmpEl = document.getElementById(pID);
			if (!tmpEl) { return; }
			tmpEl.className = tmpModeClass;
			if (!pList.length) { tmpEl.innerHTML = '<div class="psh-empty">' + _escapeHTML(pEmptyText) + '</div>'; return; }
			tmpEl.innerHTML = pList.map((pT) => tmpCards ? this._cardHTML(pT) : this._rowHTML(pT)).join('');
		};

		fFill('psh-all', tmpAll, this._query ? 'No plan sheets match.' : 'No plan sheets.');
		fFill('psh-pinned', tmpPinned, 'No pinned plan sheets match.');

		// Hide the whole Pinned section when nothing is pinned (nothing to show, no empty state needed).
		let tmpPinnedSec = document.getElementById('psh-pinned-sec');
		if (tmpPinnedSec) { tmpPinnedSec.style.display = tmpPinned.length ? '' : 'none'; }

		let fText = (pID, pText) => { let tmpEl = document.getElementById(pID); if (tmpEl) { tmpEl.textContent = pText; } };
		fText('psh-pcount', String(tmpPinned.length));
		fText('psh-acount', String(tmpAll.length));
		let tmpTotalPinned = this._tenants.filter((pT) => pT.Pinned).length;
		fText('psh-count', this._tenants.length + ' plan ' + (this._tenants.length === 1 ? 'sheet' : 'sheets') + (tmpTotalPinned ? (', ' + tmpTotalPinned + ' pinned') : ''));
	}

	_pinSVG(pOn)
	{
		return '<svg viewBox="0 0 16 16" width="15" height="15" fill="' + (pOn ? 'currentColor' : 'none') + '" stroke="currentColor" stroke-width="1.4">'
			+ '<path d="M5 2h6l-1 5 3 3H3l3-3-1-5z"/><path d="M8 10v4"/></svg>';
	}

	// The reference (view instance) used by the inline handlers on painted markup.
	_ref() { return "_Pict.views['Plansheet-Home']"; }

	_pinButtonHTML(pTenant)
	{
		let tmpRef = this._ref();
		let tmpOn = pTenant.Pinned ? ' on' : '';
		let tmpTitle = pTenant.Pinned ? 'Unpin' : 'Pin to the top';
		return '<button class="psh-pin' + tmpOn + '" title="' + tmpTitle + '" onclick="event.stopPropagation();' + tmpRef + '.togglePin(' + (pTenant.IDCustomer || 0) + ')">' + this._pinSVG(pTenant.Pinned) + '</button>';
	}

	_roleHTML(pTenant)
	{
		let tmpRole = pTenant.RoleLabel || 'Member';
		let tmpOwner = (tmpRole === 'Owner' || tmpRole === 'Platform Admin') ? ' owner' : '';
		return '<span class="psh-role' + tmpOwner + '">' + _escapeHTML(tmpRole) + '</span>';
	}

	_rowHTML(pTenant)
	{
		let tmpRef = this._ref();
		let tmpAgo = this._ago(pTenant.LastVisited);
		let tmpCurrent = pTenant.IsCurrent ? '<span class="psh-here">Current</span>' : '';
		let tmpSub = pTenant.Description
			? _escapeHTML(pTenant.Description)
			: ('as ' + _escapeHTML(pTenant.DisplayName || 'you') + (pTenant.Code ? (' &middot; ' + _escapeHTML(pTenant.Code)) : ''));
		return '<div class="psh-lrow' + (pTenant.IsCurrent ? ' is-current' : '') + '" onclick="' + tmpRef + '.openSheet(' + (pTenant.IDCustomer || 0) + ')">'
			+ '<span class="psh-mk">' + _escapeHTML(_mark(pTenant.CustomerName)) + '</span>'
			+ '<span class="psh-main"><span class="psh-name">' + _escapeHTML(pTenant.CustomerName || 'Untitled') + tmpCurrent + '</span>'
				+ '<span class="psh-sub">' + tmpSub + '</span></span>'
			+ this._roleHTML(pTenant)
			+ '<span class="psh-right"><span class="psh-meta" title="Last visited">' + _escapeHTML(tmpAgo) + '</span>' + this._pinButtonHTML(pTenant) + '</span>'
			+ '</div>';
	}

	// The navy cover box for a card: the plan sheet's primary blueprint rendered as a compact title
	// block (mirrors BlueprintRender's palette), or the pick-a-blueprint placeholder when none is set.
	_coverHTML(pTenant)
	{
		let tmpBP = pTenant.PrimaryBlueprint;
		if (!tmpBP)
		{
			return '<div class="psh-bp psh-bp-empty"><div class="psh-bp-kick">No cover blueprint</div>'
				+ '<div class="psh-bp-hint">Pick a blueprint to represent this plan sheet</div></div>';
		}
		// Flatten the blueprint's grouped pins into a few rows (type glyph + item title), capped so the
		// box stays a summary rather than the full sheet.
		let tmpRows = [];
		let tmpGroups = Array.isArray(tmpBP.Groups) ? tmpBP.Groups : [];
		for (let g = 0; g < tmpGroups.length && tmpRows.length < 4; g++)
		{
			let tmpItems = Array.isArray(tmpGroups[g].Items) ? tmpGroups[g].Items : [];
			for (let i = 0; i < tmpItems.length && tmpRows.length < 4; i++)
			{
				tmpRows.push('<div class="psh-bp-pin"><span class="psh-bp-k">' + _escapeHTML(_typeGlyph(tmpGroups[g].Type)) + '</span>'
					+ '<span class="psh-bp-pt">' + _escapeHTML(tmpItems[i].Title || '') + '</span></div>');
			}
		}
		let tmpPins = tmpRows.length ? ('<div class="psh-bp-pins">' + tmpRows.join('') + '</div>') : '';
		let tmpStrip = pTenant.Code
			? ('<div class="psh-bp-strip"><span class="psh-bp-lab">Plan sheet</span><span class="psh-bp-val">' + _escapeHTML(pTenant.Code) + '</span></div>')
			: '';
		return '<div class="psh-bp"><div class="psh-bp-kick">Blueprint</div>'
			+ '<div class="psh-bp-name">' + _escapeHTML(tmpBP.Title || 'Cover') + '</div>'
			+ tmpPins + tmpStrip + '</div>';
	}

	_cardHTML(pTenant)
	{
		let tmpRef = this._ref();
		let tmpAgo = this._ago(pTenant.LastVisited);
		let tmpCurrent = pTenant.IsCurrent ? '<span class="psh-here">Current</span>' : '';
		let tmpCode = pTenant.Code ? ('<span class="psh-code">' + _escapeHTML(pTenant.Code) + '</span>') : '';
		let tmpBlurb = pTenant.Description ? ('<div class="psh-blurb">' + _escapeHTML(pTenant.Description) + '</div>') : '';
		let tmpAgoLine = tmpAgo ? ('<span class="psh-dot"></span><span>Visited ' + _escapeHTML(tmpAgo) + '</span>') : '';
		return '<div class="psh-card" onclick="' + tmpRef + '.openSheet(' + (pTenant.IDCustomer || 0) + ')">'
			+ this._coverHTML(pTenant)
			+ '<div class="psh-card-body">'
				+ '<div class="psh-card-top">' + this._pinButtonHTML(pTenant)
					+ '<span class="psh-title">' + _escapeHTML(pTenant.CustomerName || 'Untitled') + tmpCode + '</span>' + tmpCurrent + '</div>'
				+ tmpBlurb
				+ '<div class="psh-card-foot">' + this._roleHTML(pTenant) + tmpAgoLine
					+ '<span class="psh-actions"><button class="psh-btn primary" onclick="event.stopPropagation();' + tmpRef + '.openSheet(' + (pTenant.IDCustomer || 0) + ')">Open</button></span>'
				+ '</div>'
			+ '</div>'
		+ '</div>';
	}
}

module.exports = PictViewHome;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-Home',
	DefaultRenderable: 'Plansheet-Home',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSS: /*css*/`
		/* Cross-tenant home. Chrome uses the app theme variables; the cover box (.psh-bp) is fixed
		   navy to mirror the app's blueprint plate (see BlueprintRender), regardless of app theme. */
		.psh-wrap { max-width: 1120px; margin: 0 auto; padding: 4px 4px 48px; color: var(--theme-color-text-primary, #1f2733); }
		.psh-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; margin: 6px 2px 8px; flex-wrap: wrap; }
		.psh-titles h1 { margin: 0; font-size: 24px; font-weight: 600; letter-spacing: -0.01em; }
		.psh-sub { color: var(--theme-color-text-secondary, #5b6577); font-size: 13.5px; margin-top: 4px; }
		.psh-sub .psh-cnt { color: var(--theme-color-text-secondary, #5b6577); }
		.psh-controls { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
		.psh-search { display: flex; align-items: center; gap: 8px; background: var(--theme-color-background-panel, #fff);
			border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 9px; padding: 7px 11px;
			color: var(--theme-color-text-secondary, #5b6577); font-size: 13px; min-width: 200px; }
		.psh-search input { background: none; border: 0; color: var(--theme-color-text-primary, #1f2733); font: inherit; outline: none; width: 100%; }
		.psh-search:focus-within { border-color: var(--theme-color-brand, #2f6fb0); }
		.psh-seg { display: inline-flex; background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-default, #d8dde6);
			border-radius: 9px; padding: 3px; gap: 2px; }
		.psh-seg button { border: 0; background: none; color: var(--theme-color-text-secondary, #5b6577); font: inherit; font-size: 13px;
			padding: 6px 11px; border-radius: 6px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
		.psh-seg button.on { background: var(--theme-color-background-selected, rgba(47,111,176,0.10)); color: var(--theme-color-brand, #2f6fb0); font-weight: 600; }
		.psh-seg svg { width: 15px; height: 15px; }

		.psh-section-label { display: flex; align-items: center; gap: 10px; text-transform: uppercase; letter-spacing: 0.09em;
			font-size: 11px; color: var(--theme-color-text-secondary, #8892a3); font-weight: 700; margin: 22px 2px 10px; }
		.psh-section-label .psh-count { color: var(--theme-color-text-secondary, #8892a3); font-weight: 600; }
		.psh-section-label::after { content: ""; flex: 1; height: 1px; background: var(--theme-color-border-subtle, #dde3ec); }

		.psh-empty { color: var(--theme-color-text-secondary, #8892a3); font-size: 13.5px; padding: 14px 4px; font-style: italic; }

		.psh-role { font-size: 10.5px; letter-spacing: 0.05em; text-transform: uppercase; padding: 2px 8px; border-radius: 999px;
			border: 1px solid var(--theme-color-border-default, #d8dde6); color: var(--theme-color-text-secondary, #5b6577); white-space: nowrap; }
		.psh-role.owner { color: var(--theme-color-brand, #2f6fb0); border-color: var(--theme-color-brand, #2f6fb0); }
		.psh-here { font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; margin-left: 8px; padding: 1px 7px; border-radius: 999px;
			background: var(--theme-color-background-selected, rgba(47,111,176,0.10)); color: var(--theme-color-brand, #2f6fb0); font-weight: 600; }
		.psh-meta { color: var(--theme-color-text-secondary, #8892a3); font-size: 12px; font-variant-numeric: tabular-nums; white-space: nowrap; }
		.psh-pin { border: 0; background: none; color: var(--theme-color-text-secondary, #a2acbd); cursor: pointer; display: inline-grid; place-items: center;
			width: 28px; height: 28px; border-radius: 7px; padding: 0; }
		.psh-pin.on { color: var(--theme-color-status-warning, #b9812f); }
		.psh-pin:hover { background: var(--theme-color-background-hover, #f2f6fc); color: var(--theme-color-brand, #2f6fb0); }

		/* ----- list (compact + expanded) ----- */
		.psh-list { display: flex; flex-direction: column; border: 1px solid var(--theme-color-border-default, #dde3ec); border-radius: 12px;
			overflow: hidden; background: var(--theme-color-background-panel, #fff); }
		.psh-lrow { display: grid; grid-template-columns: 30px 1fr auto auto; align-items: center; gap: 14px; padding: 12px 16px;
			border-top: 1px solid var(--theme-color-border-subtle, #eef1f6); cursor: pointer; }
		.psh-lrow:first-child { border-top: 0; }
		.psh-lrow:hover { background: var(--theme-color-background-hover, #f2f6fc); }
		.psh-lrow.is-current { background: var(--theme-color-background-selected, rgba(47,111,176,0.06)); }
		.psh-mk { width: 30px; height: 30px; border-radius: 8px; background: #16294b; border: 1px solid rgba(150,180,220,0.35);
			display: grid; place-items: center; color: #cfe0f6; font-size: 11px; font-weight: 800; letter-spacing: 0.02em; }
		.psh-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
		.psh-name { font-weight: 600; font-size: 14.5px; display: flex; align-items: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.psh-sub { color: var(--theme-color-text-secondary, #5b6577); font-size: 12.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: none; }
		.psh-right { display: flex; align-items: center; gap: 10px; justify-content: flex-end; }
		/* Compact tightens rows and hides the sub-line; expanded shows the description / persona line. */
		.psh-list.psh-compact .psh-lrow { padding: 9px 16px; }
		.psh-list.psh-compact .psh-mk { width: 26px; height: 26px; }
		.psh-list.psh-expanded .psh-lrow { padding: 14px 16px; }
		.psh-list.psh-expanded .psh-sub { display: block; }

		/* ----- cards (full-width, blueprint cover on the left) ----- */
		.psh-cards { display: grid; grid-template-columns: 1fr; gap: 14px; }
		.psh-card { display: grid; grid-template-columns: 280px 1fr; gap: 0; border: 1px solid var(--theme-color-border-default, #dde3ec);
			border-radius: 12px; overflow: hidden; background: var(--theme-color-background-panel, #fff); cursor: pointer; }
		.psh-card:hover { border-color: var(--theme-color-brand, #2f6fb0); }
		.psh-card-body { padding: 16px 18px; display: flex; flex-direction: column; gap: 9px; min-width: 0; }
		.psh-card-top { display: flex; align-items: center; gap: 10px; }
		.psh-title { font-size: 16.5px; font-weight: 700; letter-spacing: -0.01em; display: flex; align-items: center; min-width: 0;
			overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.psh-code { color: var(--theme-color-text-secondary, #8892a3); font-weight: 600; font-size: 12.5px; margin-left: 8px; letter-spacing: 0.03em; }
		.psh-blurb { color: var(--theme-color-text-secondary, #5b6577); font-size: 13px; max-width: 64ch; }
		.psh-card-foot { display: flex; align-items: center; gap: 12px; margin-top: auto; padding-top: 4px;
			color: var(--theme-color-text-secondary, #5b6577); font-size: 12px; flex-wrap: wrap; }
		.psh-card-foot .psh-dot { width: 3px; height: 3px; border-radius: 50%; background: var(--theme-color-text-secondary, #a2acbd); }
		.psh-actions { display: flex; gap: 8px; margin-left: auto; }
		.psh-btn { border: 1px solid var(--theme-color-border-default, #d8dde6); background: var(--theme-color-background-panel, #fff);
			color: var(--theme-color-text-primary, #1f2733); font: inherit; font-size: 13px; padding: 6px 14px; border-radius: 8px; cursor: pointer; }
		.psh-btn.primary { background: var(--theme-color-brand, #2f6fb0); border-color: var(--theme-color-brand, #2f6fb0); color: #fff; font-weight: 600; }
		.psh-btn.primary:hover { filter: brightness(1.05); }

		/* the blueprint cover box -- fixed navy, mirrors BlueprintRender's plate palette */
		.psh-bp { background: #16294b; border-right: 1px solid var(--theme-color-border-default, #dde3ec); padding: 15px 16px;
			display: flex; flex-direction: column; gap: 9px; position: relative; color: #eef4fc; }
		.psh-bp::before { content: ""; position: absolute; inset: 9px; border: 1px solid rgba(208,226,247,0.30); border-radius: 8px; pointer-events: none; }
		.psh-bp-kick { font-family: ui-monospace, Menlo, monospace; text-transform: uppercase; letter-spacing: 0.16em; font-size: 10px; font-weight: 700; color: #88a8d6; position: relative; }
		.psh-bp-name { font-size: 14.5px; font-weight: 700; color: #eef4fc; position: relative; overflow-wrap: anywhere; }
		.psh-bp-pins { display: flex; flex-direction: column; gap: 6px; position: relative; margin-top: 2px; }
		.psh-bp-pin { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #cfe0f6; min-width: 0; }
		.psh-bp-k { flex: 0 0 auto; width: 19px; height: 19px; border-radius: 5px; background: rgba(150,180,220,0.16); border: 1px solid rgba(150,180,220,0.35);
			display: grid; place-items: center; font-size: 9px; font-weight: 800; color: #88a8d6; font-family: ui-monospace, Menlo, monospace; }
		.psh-bp-pt { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.psh-bp-strip { display: flex; flex-direction: column; gap: 1px; margin-top: auto; padding-top: 9px;
			border-top: 1px solid rgba(150,180,220,0.28); position: relative; }
		.psh-bp-lab { text-transform: uppercase; letter-spacing: 0.08em; color: #88a8d6; font-size: 9px; font-weight: 700; font-family: ui-monospace, Menlo, monospace; }
		.psh-bp-val { color: #eef4fc; font-weight: 600; font-size: 12px; margin-top: 1px; }
		.psh-bp-empty { align-items: center; justify-content: center; text-align: center; min-height: 130px; }
		.psh-bp-empty .psh-bp-kick { margin-bottom: 4px; }
		.psh-bp-hint { color: #7f97bd; font-size: 12.5px; position: relative; max-width: 22ch; }

		@media (max-width: 720px)
		{
			.psh-card { grid-template-columns: 1fr; }
			.psh-bp { border-right: 0; border-bottom: 1px solid var(--theme-color-border-default, #dde3ec); }
			.psh-lrow { grid-template-columns: 26px 1fr auto; }
			.psh-lrow .psh-role { display: none; }
		}
	`,
	Templates:
	[
		{
			Hash: 'Plansheet-Home',
			Template: /*html*/`
<div class="psh-wrap" id="Plansheet-Home-Root">
	<div class="psh-head">
		<div class="psh-titles">
			<h1>Your plan sheets</h1>
			<div class="psh-sub">Everything you are subscribed to, across every plan sheet. <span class="psh-cnt" id="psh-count"></span></div>
		</div>
		<div class="psh-controls">
			<div class="psh-search"><span aria-hidden="true">&#9906;</span><input id="psh-search" type="search" placeholder="Filter plan sheets..." autocomplete="off" oninput="_Pict.views['Plansheet-Home'].filter(this.value)" /></div>
			<div class="psh-seg" role="tablist" aria-label="View mode">
				<button data-mode="compact" title="Compact list" onclick="_Pict.views['Plansheet-Home'].setMode('compact')"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 4h12M2 8h12M2 12h12"/></svg>Compact</button>
				<button data-mode="expanded" title="Expanded list" onclick="_Pict.views['Plansheet-Home'].setMode('expanded')"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 3.5h12M2 8h12M2 12.5h12"/><path d="M2 5.5h8" opacity=".5"/><path d="M2 10h8" opacity=".5"/></svg>Expanded</button>
				<button data-mode="cards" title="Blueprint cards" onclick="_Pict.views['Plansheet-Home'].setMode('cards')"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="3" width="12" height="4.5" rx="1"/><rect x="2" y="9" width="12" height="4.5" rx="1"/></svg>Cards</button>
			</div>
		</div>
	</div>

	<div class="psh-section" id="psh-pinned-sec" style="display:none">
		<div class="psh-section-label">Pinned <span class="psh-count" id="psh-pcount"></span></div>
		<div id="psh-pinned" class="psh-list psh-compact"></div>
	</div>

	<div class="psh-section">
		<div class="psh-section-label">All plan sheets <span class="psh-count" id="psh-acount"></span></div>
		<div id="psh-all" class="psh-list psh-compact"></div>
	</div>
</div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'Plansheet-Home',
			TemplateHash: 'Plansheet-Home',
			ContentDestinationAddress: '#Plansheet-Workspace',
			RenderMethod: 'replace'
		}
	]
};
